import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Alert, AlertDescription } from '../components/ui/alert';
import { 
  Loader2, CheckCircle, AlertTriangle, TrendingUp, Package, 
  Calendar, CreditCard, ArrowRight, Award, RefreshCw
} from 'lucide-react';
import Navbar from '../components/layout/Navbar';
import Footer from '../components/layout/Footer';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

export default function DealerDashboardPage() {
  const { user, token, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  
  const [dealerStatus, setDealerStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    
    fetchDealerStatus();
  }, [isAuthenticated, navigate, token]);

  const fetchDealerStatus = async () => {
    try {
      const response = await axios.get(`${API}/api/dealer/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDealerStatus(response.data);
      
      // Redirect non-dealers
      if (response.data.dealer_status === 'none') {
        navigate('/dealer-program');
      }
    } catch (err) {
      setError('Failed to load dealer status');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'approved':
        return <Badge className="bg-green-100 text-green-800">Active Dealer</Badge>;
      case 'pending':
        return <Badge className="bg-blue-100 text-blue-800">Pending Approval</Badge>;
      case 'inactive':
        return <Badge className="bg-amber-100 text-amber-800">Inactive</Badge>;
      case 'rejected':
        return <Badge className="bg-red-100 text-red-800">Application Declined</Badge>;
      default:
        return <Badge variant="outline">Not a Dealer</Badge>;
    }
  };

  const getTierBadge = (tier) => {
    switch (tier) {
      case 'premium':
        return <Badge className="bg-purple-100 text-purple-800">Premium Tier</Badge>;
      case 'volume':
        return <Badge className="bg-blue-100 text-blue-800">Volume Tier</Badge>;
      case 'standard':
      default:
        return <Badge className="bg-gray-100 text-gray-800">Standard Tier</Badge>;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navbar />
        <div className="flex items-center justify-center py-20">
          <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
        </div>
        <Footer />
      </div>
    );
  }

  // Pending application view
  if (dealerStatus?.dealer_status === 'pending') {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navbar />
        <div className="max-w-4xl mx-auto px-4 py-8">
          <Card className="shadow-lg">
            <CardContent className="p-8 text-center">
              <Loader2 className="h-16 w-16 text-blue-500 mx-auto mb-4" />
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Application Under Review</h2>
              <p className="text-gray-600 mb-4">
                Your dealer application is being reviewed. We will notify you by email once a decision is made.
              </p>
              {dealerStatus.application && (
                <p className="text-sm text-gray-500">
                  Submitted: {new Date(dealerStatus.application.created_at).toLocaleDateString()}
                </p>
              )}
            </CardContent>
          </Card>
        </div>
        <Footer />
      </div>
    );
  }

  // Inactive dealer view
  if (dealerStatus?.dealer_status === 'inactive') {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navbar />
        <div className="max-w-4xl mx-auto px-4 py-8">
          <Alert className="mb-6 border-amber-200 bg-amber-50">
            <AlertTriangle className="h-4 w-4 text-amber-600" />
            <AlertDescription className="text-amber-800">
              Your dealer account is currently inactive. Submit 250+ cards to request reactivation.
            </AlertDescription>
          </Alert>
          
          <Card className="shadow-lg">
            <CardContent className="p-8 text-center">
              <AlertTriangle className="h-16 w-16 text-amber-500 mx-auto mb-4" />
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Dealer Account Inactive</h2>
              <p className="text-gray-600 mb-6">
                Your dealer privileges have been paused due to not meeting the monthly minimum requirements.
                You can still submit cards at standard pricing, or reapply for dealer status.
              </p>
              <div className="flex gap-4 justify-center">
                <Button onClick={() => navigate('/submission/new')}>
                  New Submission
                </Button>
                <Button variant="outline" onClick={() => navigate('/dealer/apply')}>
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Reapply
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
        <Footer />
      </div>
    );
  }

  // Rejected view with reapply option
  if (dealerStatus?.dealer_status === 'rejected') {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navbar />
        <div className="max-w-4xl mx-auto px-4 py-8">
          <Card className="shadow-lg">
            <CardContent className="p-8 text-center">
              <AlertTriangle className="h-16 w-16 text-red-400 mx-auto mb-4" />
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Application Declined</h2>
              {dealerStatus.application?.rejection_reason && (
                <p className="text-gray-600 mb-4">
                  Reason: {dealerStatus.application.rejection_reason}
                </p>
              )}
              
              {dealerStatus.can_reapply ? (
                <div>
                  <p className="text-gray-600 mb-6">
                    You&apos;re eligible to reapply for the dealer program.
                  </p>
                  <Button onClick={() => navigate('/dealer/apply')}>
                    <RefreshCw className="h-4 w-4 mr-2" />
                    Reapply Now
                  </Button>
                </div>
              ) : (
                <p className="text-gray-500">
                  You can reapply after: {new Date(dealerStatus.can_reapply_at).toLocaleDateString()}
                </p>
              )}
            </CardContent>
          </Card>
        </div>
        <Footer />
      </div>
    );
  }

  // Active dealer dashboard
  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      
      <div className="max-w-6xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Dealer Dashboard</h1>
            <p className="text-gray-600 mt-1">Manage your dealer account and track your submissions</p>
          </div>
          <div className="mt-4 md:mt-0 flex gap-2">
            {getStatusBadge(dealerStatus?.dealer_status)}
            {getTierBadge(dealerStatus?.dealer_tier)}
          </div>
        </div>

        {/* Warning if below monthly minimum */}
        {dealerStatus?.warning_count > 0 && (
          <Alert className="mb-6 border-amber-200 bg-amber-50">
            <AlertTriangle className="h-4 w-4 text-amber-600" />
            <AlertDescription className="text-amber-800">
              <strong>Warning:</strong> You&apos;re below the 250 card monthly minimum. 
              {dealerStatus.warning_count === 1 && ' This is your first warning.'}
              {dealerStatus.warning_count === 2 && ' This is your second warning. One more month below minimum will result in account deactivation.'}
              {' '}Submit more cards to maintain your dealer status.
            </AlertDescription>
          </Alert>
        )}

        {/* Stats Grid */}
        <div className="grid md:grid-cols-3 gap-6 mb-8">
          {/* Monthly Progress */}
          <Card className="shadow-md">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-gray-500 flex items-center">
                <Calendar className="h-4 w-4 mr-2" />
                This Month
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-gray-900 mb-2">
                {dealerStatus?.monthly_card_count || 0}
                <span className="text-lg text-gray-500 font-normal"> / 250 cards</span>
              </div>
              <Progress 
                value={dealerStatus?.monthly_progress_percent || 0} 
                className="h-2 mb-2"
              />
            <p className="text-sm text-gray-500">
              {dealerStatus?.monthly_progress_percent >= 100 
                ? <span className="text-green-600 flex items-center"><CheckCircle className="h-4 w-4 mr-1" /> Monthly minimum met!</span>
                : `${250 - (dealerStatus?.monthly_card_count || 0)} more cards needed`
              }
            </p>
            </CardContent>
          </Card>

          {/* All-Time Stats */}
          <Card className="shadow-md">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-gray-500 flex items-center">
                <TrendingUp className="h-4 w-4 mr-2" />
                All-Time Total
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-gray-900 mb-1">
                {dealerStatus?.all_time_cards?.toLocaleString() || 0}
              </div>
              <p className="text-sm text-gray-500">
                cards across {dealerStatus?.all_time_submissions || 0} submissions
              </p>
            </CardContent>
          </Card>

          {/* Current Tier Info */}
          <Card className="shadow-md">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-gray-500 flex items-center">
                <Award className="h-4 w-4 mr-2" />
                Your Tier
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-xl font-bold text-gray-900 mb-1 capitalize">
                {dealerStatus?.dealer_tier || 'Standard'} Dealer
              </div>
              <p className="text-sm text-gray-500">
                {dealerStatus?.dealer_tier === 'standard' && '250-499 cards per submission'}
                {dealerStatus?.dealer_tier === 'volume' && '500-999 cards per submission'}
                {dealerStatus?.dealer_tier === 'premium' && '1,000+ cards with MGP'}
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Pricing Info */}
        <Card className="shadow-md mb-8">
          <CardHeader>
            <CardTitle className="flex items-center">
              <CreditCard className="h-5 w-5 mr-2" />
              Your Dealer Pricing
            </CardTitle>
            <CardDescription>
              Special pricing based on your submission size
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-3 gap-4">
              <div className={`p-4 rounded-lg border ${dealerStatus?.dealer_tier === 'standard' ? 'bg-blue-50 border-blue-200' : 'bg-gray-50'}`}>
                <div className="font-semibold text-gray-900">Dealer Standard</div>
                <div className="text-2xl font-bold text-blue-600">$8.00<span className="text-sm text-gray-500">/card</span></div>
                <div className="text-sm text-gray-500">250-499 cards</div>
              </div>
              <div className={`p-4 rounded-lg border ${dealerStatus?.dealer_tier === 'volume' ? 'bg-blue-50 border-blue-200' : 'bg-gray-50'}`}>
                <div className="font-semibold text-gray-900">Dealer Volume</div>
                <div className="text-2xl font-bold text-blue-600">$6.00<span className="text-sm text-gray-500">/card</span></div>
                <div className="text-sm text-gray-500">500-999 cards</div>
              </div>
              <div className={`p-4 rounded-lg border ${dealerStatus?.dealer_tier === 'premium' ? 'bg-purple-50 border-purple-200' : 'bg-gray-50'}`}>
                <div className="font-semibold text-gray-900">Dealer Premium</div>
                <div className="text-2xl font-bold text-purple-600">$6.00<span className="text-sm text-gray-500">/card</span></div>
                <div className="text-sm text-gray-500">1,000+ cards with MGP</div>
                <Badge className="mt-2 bg-purple-100 text-purple-800 text-xs">Includes Minimum Grade Protection</Badge>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Quick Actions */}
        <div className="grid md:grid-cols-2 gap-6">
          <Card className="shadow-md hover:shadow-lg transition-shadow cursor-pointer" onClick={() => navigate('/submission/new')}>
            <CardContent className="p-6 flex items-center justify-between">
              <div className="flex items-center">
                <div className="bg-blue-100 p-3 rounded-lg mr-4">
                  <Package className="h-6 w-6 text-blue-600" />
                </div>
                <div>
                  <div className="font-semibold text-lg">New Submission</div>
                  <div className="text-gray-500 text-sm">Start a new grading order at dealer pricing</div>
                </div>
              </div>
              <ArrowRight className="h-5 w-5 text-gray-400" />
            </CardContent>
          </Card>

          <Card className="shadow-md hover:shadow-lg transition-shadow cursor-pointer" onClick={() => navigate('/dashboard')}>
            <CardContent className="p-6 flex items-center justify-between">
              <div className="flex items-center">
                <div className="bg-green-100 p-3 rounded-lg mr-4">
                  <TrendingUp className="h-6 w-6 text-green-600" />
                </div>
                <div>
                  <div className="font-semibold text-lg">View Submissions</div>
                  <div className="text-gray-500 text-sm">Track your current and past orders</div>
                </div>
              </div>
              <ArrowRight className="h-5 w-5 text-gray-400" />
            </CardContent>
          </Card>
        </div>

        {/* Member Since */}
        {dealerStatus?.dealer_approved_at && (
          <p className="text-center text-gray-500 text-sm mt-8">
            Dealer since {new Date(dealerStatus.dealer_approved_at).toLocaleDateString()}
          </p>
        )}
      </div>

      <Footer />
    </div>
  );
}
